import type { AxiosResponse } from "axios";

export enum Flows {
	SIGNIN = "signin",
	SIMPLE_SIGNIN = "simple_signin",
	SIGNIN_OLD_CP = "signin_old_cp",
	SIGNIN_CROSS = "signin_cross",
	SIGNIN_UNIFIED = "signin_unified",
	SIGNUP = "signup",
	SIGNUP_NCAD_COMPLETED = "signup_ncad_completed",
	SIGNUP_UNIFIED = "signup_unified",
	SIGNUP_NCAD = "signup_ncad",
	SIGN_UP_PARTNER = "signup_partner",

	// EXTENDED/SUB FLOWS	- no handled by /document endpoint
	PASSWORD_REQUIRED = "password_required",
	SIGNIN_UNIFY_PASSWORD = "signin_unify_password",
	VALIDATE_CONFLICTING_EMAIL = "validate_conflicting_email",
	UPDATE_EXPIRED_EMAIL = "update_expired_email",
	MFA_REQUIRED = "mfa_required",
	MFA_EMAIL_REQUIRED = "mfa_email_required",
	EXTEND_FLOW = "extend_flow",
	RECOVERY_PASSWORD = "recovery_password",
	RESET_PASSWORD = "reset_password",
	RESET_PASSWORD_CP = "reset_password_CP",
	UNIFICATION_SIGNUP_CONTEXT = "unification_signup_context",
	UNIFICATION_SIGNIN_CONTEXT = "unification_signin_context",
	UPDATE_CONTACT = "update_contact",
	UPDATE_PASSWORD = "update_password",
	SIGNIN_CROSS_COMPLEX_PASSWORD = "signin_cross_complex_password",
	SIGNIN_CROSS_DISTINCT = "signin_cross_distinct",
	UNLOCK_PROTECTED_FEATURE = "unlock_protected_feature",
	PUBLIC_EMAIL_CONFIRMATION = "public_email_confirmation",

	// PRIVATE FLOWS
	PRIVATE_MFA_POSITIVE_DATA = "private_mfa_positive_data",
}

export type FormType =
	| "recovery-password"
	| "reset-password"
	| "reset-password-ac-from-cp"
	| "sign-up"
	| "sign-up-ncad"
	| "sign-up-ncad-completed"
	| "sign-up-partner"
	| "unification-sign-up"
	| "sign-in"
	| "sign-in-cross"
	| "sign-in-unify-password"
	| "validate-conflicting-email"
	| "update-contact"
	| "update-password"
	| "simple-sign-in"
	| "unlock-protected-feature"
	| "public-email-confirmation"
	| "private-mfa-positive-data";

export enum FormTypeEnum {
	RECOVERY_PASSWORD = "recovery-password",
	RESET_PASSWORD = "reset-password",
	RESET_PASSWORD_AC_FROM_CP = "reset-password-ac-from-cp",
	SIGN_UP = "sign-up",
	SIGN_UP_NCAD = "sign-up-ncad",
	SIGN_UP_NCAD_COMPLETED = "sign-up-ncad-completed",
	SIGN_UP_PARTNER = "sign-up-partner",
	SIGN_UP_UNIFICATION = "unification-sign-up",
	SIGN_IN = "sign-in",
	SIGN_IN_CROSS = "sign-in-cross",
	SIGN_IN_UNIFY_PASSWORD = "sign-in-unify-password",
	VALIDATE_CONFLICTING_EMAIL = "validate-conflicting-email",
	UPDATE_CONTACT = "update-contact",
	UPDATE_PASSWORD = "update-password",
	UNLOCK_PROTECTED_FEATURE = "unlock-protected-feature",
	SIMPLE_SIGN_IN = "simple-sign-in",
	PUBLIC_EMAIL_CONFIRMATION = "public-email-confirmation",
	PRIVATE_MFA_POSITIVE_DATA = "private-mfa-positive-data",
}

export enum SubmitStepCTALabelEnum {
	CONTINUE = "Continuar",
	NEW_PASSWORD = "Cadastrar senha",
}

export type AuthTypes = "sign-in" | "sign-up" | "email-sign-in";

export interface UserResponseProps {
	celular: string;
	customerIdHash: string;
	cutOffValue: string;
	dataNascimento: string;
	documento: string;
	email: string;
	id: number;
	newsletter: boolean;
	nome: string;
	primeiroNome: string;
	nomeSocial: string;
	registrado: string;
	sexo: string;
	telefoneValidado: string;
	termsOfUse: string;
	termsOfUseVersion: number;
	uuid: string;
	admin: boolean;
	createdAt?: string;
}

export type StepsActions =
	| "DocumentStep"
	| "KBAStep"
	| "KBAStep-1"
	| "KBAStep-2"
	| "EmailStep"
	| "PhoneStep"
	| "MFAVerificationCodeStep"
	| "ValidationMethodsStep"
	| "PasswordStep"
	| "UnificationSignUpContextStep"
	| "UnificationContextStep"
	| "VerificationCodeStep"
	| "UpdateContactStep"
	| "UpdatePasswordStep"
	| "UnifyPasswordStep"
	| "CompleteSignUpNCADStep"
	| "MFAEmailVerificationCodeStep"
	| "ValidateIdentityContextStep"
	| "BirthDateStep";

export interface CallBackNextStepsActionsProps {
	step: StepsActions;
	status: "success" | "error";
	error: {
		error_code: string;
		message: string;
		error_slug: string;
		fields: { field_name: string; slug: string }[];
		document?: string;
		birthDate?: string;
	};
	data: AxiosResponse;
	nextStepActions: { [key in StepsActions]: (response: AxiosResponse) => void };
	errorNextStepActions: Partial<{
		[key in StepsActions]: (response: StepActionError) => void;
	}>;
}

export type NextStepActions<T> = {
	[key in StepsActions]: (payload: T) => void;
};

export type StepActionError = {
	error_code: string;
	message: string;
	error_slug: string;
	fields: { field_name: string; slug: string }[];
	document?: string;
	birthDate?: string;
};

export type FlowInfos = {
	[key: string]: {
		redlineAppName: string;
		homePath: string;
		unificationValidateEmail?: string;
		unificationUpdateEmail?: string;
		unificationUpdatePhone?: string;
		unificationUnifyPassword?: string;
	};
};

export type FlowComponentProps = {
	onSuccess?: (data: unknown) => void;
};

export type SignUpFlowComponentProps = {
	onSuccess?: (data: unknown) => void;
	setIsAuthenticatedAfterUnificationContext?: () => void;
};

export type Credentials = {
	access_token: string;
	refresh_token: string;
};

export type UpdateContactType = "email" | "phone" | "socialName";

export type UpdateContactProps = {
	contactType: UpdateContactType;
	signOut: () => Promise<void>;
	getValidCredentials?: () => Promise<Credentials | null>;
};

export type UpdateContactFlowComponentProps = FlowComponentProps &
	UpdateContactProps;
export type UpdatePasswordFlowComponentProps = FlowComponentProps & {
	signOut: () => Promise<void>;
	getValidCredentials?: () => Promise<Credentials | null>;
};
